import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../context/GoogleMapsProvider';
import { sendMemberBookingQuote } from '../../utils/marketingApi';
import { QUOTE_MAX_VEHICLE_CLASSES, QUOTE_RATE_CLASS_MAX } from '../../bookingQuote/config';
import {
  applyLocationSuggestion,
  fetchCustomerAddressRecommendations,
  fetchQuoteVehicleOptions,
  formatQuoteListPrice,
  formatQuotePickupTime,
  recommendedAddressFromSaved,
  recommendedAddressSubtitle,
  recommendedAddressTitle,
  resolveLocationByCoordinates,
  searchLocationByText,
} from '../../utils/bookingQuoteFlow';
import {
  buildBookingPayload,
  createBooking,
  defaultPickupTime,
  fetchCustomerDetails,
  normalizeDiscountPercent,
  saveDraftBooking,
} from '../../utils/bookingQuoteBooking';
import { getEffectiveAmount, getPriceBreakdown } from '../../utils/bookingQuotePricing';
import { memberCustomerId, resolveCustomerIdByPhone } from '../../utils/resolveCustomerIdByPhone';
import s from './BookingQuoteWaModal.module.scss';

const FLEX_DISCOUNT_PERCENT = 25;
const FIRST_BOOKING_DISCOUNT_PERCENT = 25;

function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const MAP_CENTER = { lat: 13.0, lng: 74.8 };
const MAP_STYLE = { width: '100%', height: '260px' };

function emptyDrop() {
  return { draft: '', address: null, marker: null };
}

export default function BookingQuoteWaModal({
  open,
  onClose,
  batchId,
  memberId,
  member,
  refollowMemberId = null,
  marketingInitiated = false,
  onSent,
  onBookingComplete,
  showToast,
}) {
  const { isLoaded, loadError } = useGoogleMaps();
  const mapRef = useRef(null);
  const [mapView, setMapView] = useState({ center: MAP_CENTER, zoom: 9 });

  const [pickupDraft, setPickupDraft] = useState('');
  const [pickup, setPickup] = useState(null);
  const [pickupMarker, setPickupMarker] = useState(null);
  const [drops, setDrops] = useState([emptyDrop()]);
  /** -1 = pickup, 0+ = drop index (New Booking) */
  const [activeMarkerIndex, setActiveMarkerIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [routeMeta, setRouteMeta] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [vehicleType, setVehicleType] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [loadWeight, setLoadWeight] = useState('');
  const [loadLength, setLoadLength] = useState('');
  const [loadWidth, setLoadWidth] = useState('');
  const [loadHeight, setLoadHeight] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [pickupTime, setPickupTime] = useState(() => defaultPickupTime());
  const [deliveryDeadline, setDeliveryDeadline] = useState(null);
  const [isFlexBooking, setIsFlexBooking] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [materialDescription, setMaterialDescription] = useState('');
  /** Index in vehicleOptions — used for draft / confirm booking */
  const [bookingVehicleIndex, setBookingVehicleIndex] = useState(null);
  /** Centered confirm step after schedule/details are valid (no duplicate datetime fields). */
  const [confirmBookingMode, setConfirmBookingMode] = useState(null);
  const bookingSubmitLockRef = useRef(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [bookingDiscountPercent, setBookingDiscountPercent] = useState('');
  const [customerDiscountPercent, setCustomerDiscountPercent] = useState(0);
  const [customerBookingCount, setCustomerBookingCount] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraftSaved(false);
    setBookingConfirmed(false);
    setQuoteSent(false);
  }, [open, memberId]);
  const [customerGstin, setCustomerGstin] = useState('');
  const [isB2bCustomer, setIsB2bCustomer] = useState(false);
  /** class id → manual price string (WhatsApp quote lines) */
  const [quotePriceOverrides, setQuotePriceOverrides] = useState({});
  /** Manual final amount for draft/confirm booking vehicle */
  const [bookingPriceOverride, setBookingPriceOverride] = useState('');

  const [resolvedCustomerId, setResolvedCustomerId] = useState(null);
  const customerId = resolvedCustomerId;
  const sourceLeadId = member?.booking_lead_id ?? member?.lead_id ?? null;
  const customerName = useMemo(
    () => member?.greeting_name || member?.name || member?.business_name || '',
    [member],
  );

  const markerKey = useMemo(() => {
    const parts = [];
    if (pickupMarker) parts.push(`p:${pickupMarker.lat.toFixed(5)},${pickupMarker.lng.toFixed(5)}`);
    drops.forEach((d, i) => {
      if (d.marker) parts.push(`d${i}:${d.marker.lat.toFixed(5)},${d.marker.lng.toFixed(5)}`);
    });
    return parts.join('|');
  }, [pickupMarker, drops]);

  const sortedVehicleOptions = useMemo(
    () => [...vehicleOptions]
      .filter((v) => Number(v.vehicle_class) <= QUOTE_RATE_CLASS_MAX)
      .sort((a, b) => Number(a.vehicle_class) - Number(b.vehicle_class)),
    [vehicleOptions],
  );

  const missingClassIds = useMemo(() => {
    const got = new Set(sortedVehicleOptions.map((v) => Number(v.vehicle_class)));
    const missing = [];
    for (let c = 1; c <= QUOTE_RATE_CLASS_MAX; c += 1) {
      if (!got.has(c)) missing.push(c);
    }
    return missing;
  }, [sortedVehicleOptions]);

  const resetPricing = useCallback(() => {
    setVehicleOptions([]);
    setRouteMeta(null);
    setSelectedClasses([]);
  }, []);

  const resetAll = useCallback(() => {
    setPickupDraft('');
    setPickup(null);
    setPickupMarker(null);
    setDrops([emptyDrop()]);
    setActiveMarkerIndex(-1);
    setSuggestions([]);
    setPickupTime(defaultPickupTime());
    setDeliveryDeadline(null);
    setIsFlexBooking(false);
    setRemarks('');
    setMaterialDescription('');
    setBookingVehicleIndex(null);
    setConfirmBookingMode(null);
    bookingSubmitLockRef.current = false;
    setDetailsOpen(false);
    setBookingDiscountPercent('');
    setCustomerDiscountPercent(0);
    setCustomerBookingCount(null);
    setCustomerGstin('');
    setIsB2bCustomer(false);
    setQuotePriceOverrides({});
    setBookingPriceOverride('');
    setMapView({ center: MAP_CENTER, zoom: 9 });
    resetPricing();
    setError(null);
  }, [resetPricing]);

  useEffect(() => {
    if (!open) {
      setResolvedCustomerId(null);
      return undefined;
    }
    let cancelled = false;
    const direct = memberCustomerId(member);
    if (direct) {
      setResolvedCustomerId(direct);
      return undefined;
    }
    const phone = member?.phone_e164 || member?.phone;
    resolveCustomerIdByPhone(phone).then((id) => {
      if (!cancelled) setResolvedCustomerId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [open, member]);

  useEffect(() => {
    if (!open) return;
    resetAll();
  }, [open, resetAll]);

  useEffect(() => {
    if (!open || !customerId) {
      if (open && !customerId) setSavedAddresses([]);
      return;
    }
    setSavedLoading(true);
    Promise.allSettled([
      fetchCustomerAddressRecommendations(customerId),
      fetchCustomerDetails(customerId),
    ])
      .then(([addrResult, detailsResult]) => {
        const addresses = addrResult.status === 'fulfilled' ? addrResult.value : [];
        const details = detailsResult.status === 'fulfilled' ? detailsResult.value : null;
        setSavedAddresses(addresses);
        const pct = normalizeDiscountPercent(
          Number(details?.discount_percent ?? details?.discount_factor ?? 0),
        );
        const bookingCount = Number(details?.booking_count ?? 0);
        setCustomerDiscountPercent(pct);
        setCustomerBookingCount(bookingCount);
        // Match New Booking auto-apply: customer stored % > first-booking 25% > none.
        if (pct > 0) {
          setBookingDiscountPercent(String(pct));
        } else if (bookingCount === 0) {
          setBookingDiscountPercent(String(FIRST_BOOKING_DISCOUNT_PERCENT));
        } else {
          setBookingDiscountPercent('');
        }
        setCustomerGstin(details?.gstin || details?.gst_in || '');
        setIsB2bCustomer(Boolean(details?.is_b2b || details?.b2b_customer));
      })
      .finally(() => setSavedLoading(false));
  }, [open, customerId]);

  useEffect(() => {
    // Auto-detect early-booking 10% discount when pickup is tomorrow or later
    // Same logic as New Booking -- not a default discount, it's a real rate policy.
    if (!pickupTime || isFlexBooking) return;
    if (customerDiscountPercent > 0 || isB2bCustomer) return;
    if (customerBookingCount === 0) return;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const pickupDate = new Date(pickupTime);
    pickupDate.setHours(0, 0, 0, 0);
    if (pickupDate >= tomorrow && normalizeDiscountPercent(bookingDiscountPercent) === 0) {
      setBookingDiscountPercent('10');
    }
  }, [
    pickupTime,
    customerDiscountPercent,
    customerBookingCount,
    isB2bCustomer,
    bookingDiscountPercent,
    isFlexBooking,
  ]);

  useEffect(() => {
    if (isFlexBooking) {
      const bestFlexDiscount = Math.max(
        FLEX_DISCOUNT_PERCENT,
        customerDiscountPercent,
        customerBookingCount === 0 ? FIRST_BOOKING_DISCOUNT_PERCENT : 0,
      );
      setBookingDiscountPercent(String(bestFlexDiscount));
    } else if (customerDiscountPercent > 0) {
      setBookingDiscountPercent(String(customerDiscountPercent));
    } else if (customerBookingCount === 0) {
      setBookingDiscountPercent(String(FIRST_BOOKING_DISCOUNT_PERCENT));
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const pickupDate = new Date(pickupTime);
      pickupDate.setHours(0, 0, 0, 0);
      if (pickupDate >= tomorrow) {
        setBookingDiscountPercent('10');
      } else {
        setBookingDiscountPercent('');
      }
    }
  }, [isFlexBooking, customerDiscountPercent, customerBookingCount, pickupTime]);

  const fitMapToMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps?.LatLngBounds) return;

    const points = [];
    if (pickupMarker) points.push(pickupMarker);
    drops.forEach((d) => {
      if (d.marker) points.push(d.marker);
    });
    if (points.length === 0) return;

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(11);
      setMapView({ center: points[0], zoom: 11 });
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      const center = map.getCenter();
      let zoom = map.getZoom() ?? 10;
      if (zoom > 12) {
        map.setZoom(12);
        zoom = 12;
      }
      setMapView({
        center: { lat: center.lat(), lng: center.lng() },
        zoom,
      });
    });
  }, [pickupMarker, drops]);

  useEffect(() => {
    if (!open || !isLoaded || !markerKey) return undefined;
    const t = window.setTimeout(() => fitMapToMarkers(), 120);
    return () => window.clearTimeout(t);
  }, [open, isLoaded, markerKey, fitMapToMarkers]);

  const applyResolvedPickup = useCallback((address) => {
    setPickup(address);
    setPickupDraft(address.text);
    if (address.location) setPickupMarker(address.location);
    setSuggestions([]);
    resetPricing();
  }, [resetPricing]);

  const applyResolvedDrop = useCallback((dropIndex, address) => {
    setDrops((prev) => {
      const next = [...prev];
      next[dropIndex] = {
        ...next[dropIndex],
        draft: address.text,
        address,
        marker: address.location || null,
      };
      return next;
    });
    setSuggestions([]);
    resetPricing();
  }, [resetPricing]);

  const applySavedAddress = useCallback((address, isPickup, dropIndex = 0) => {
    const resolved = recommendedAddressFromSaved(address);
    if (!resolved.id) {
      setError('Saved address missing id');
      return;
    }
    if (isPickup) {
      applyResolvedPickup(resolved);
    } else {
      applyResolvedDrop(dropIndex, resolved);
    }
    if (resolved.location) {
      setTimeout(fitMapToMarkers, 100);
    }
  }, [applyResolvedPickup, applyResolvedDrop, fitMapToMarkers]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setTimeout(fitMapToMarkers, 50);
  }, [fitMapToMarkers]);

  const handleMapClick = useCallback(async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const isPickup = activeMarkerIndex === -1;
    const dropIndex = isPickup ? 0 : activeMarkerIndex;
    setError(null);
    setBusy('map');
    try {
      const result = await resolveLocationByCoordinates(lat, lng);
      if (result.kind === 'similar') {
        setSuggestions(result.matches);
        return;
      }
      if (isPickup) {
        applyResolvedPickup(result.address);
      } else {
        applyResolvedDrop(dropIndex, result.address);
      }
      setTimeout(fitMapToMarkers, 100);
    } catch (e) {
      setError(e.message || 'Map resolve failed');
    } finally {
      setBusy(null);
    }
  }, [activeMarkerIndex, applyResolvedPickup, applyResolvedDrop, fitMapToMarkers]);

  const vehicleParams = useMemo(() => ({
    vehicle_name: vehicleType.trim() || undefined,
    body_type: bodyType || undefined,
    load_weight: loadWeight || undefined,
    load_length: loadLength || undefined,
    load_width: loadWidth || undefined,
    load_height: loadHeight || undefined,
  }), [vehicleType, bodyType, loadWeight, loadLength, loadWidth, loadHeight]);

  const hasVehicleParams = Object.values(vehicleParams).some(Boolean);

  const resolvedDropIds = useMemo(
    () => drops.map((d) => d.address?.id).filter(Boolean),
    [drops],
  );

  const allDropsResolved = drops.length > 0 && resolvedDropIds.length === drops.length;

  const runSearch = async (isPickup, dropIndex = 0) => {
    const text = isPickup ? pickupDraft : drops[dropIndex]?.draft;
    setError(null);
    setBusy(isPickup ? 'search-pickup' : `search-drop-${dropIndex}`);
    setActiveMarkerIndex(isPickup ? -1 : dropIndex);
    try {
      const result = await searchLocationByText(text);
      if (result.kind === 'similar') {
        setSuggestions(result.matches);
        return;
      }
      if (isPickup) {
        applyResolvedPickup(result.address);
      } else {
        applyResolvedDrop(dropIndex, result.address);
      }
      setTimeout(fitMapToMarkers, 100);
    } catch (e) {
      setError(e.message || 'Search failed');
      setSuggestions([]);
    } finally {
      setBusy(null);
    }
  };

  const pickSuggestion = async (suggestion) => {
    const isPickup = activeMarkerIndex === -1;
    const dropIndex = isPickup ? 0 : activeMarkerIndex;
    setError(null);
    setBusy('suggestion');
    try {
      const address = await applyLocationSuggestion(suggestion);
      if (isPickup) {
        applyResolvedPickup(address);
      } else {
        applyResolvedDrop(dropIndex, address);
      }
      setTimeout(fitMapToMarkers, 100);
    } catch (e) {
      setError(e.message || 'Could not use that address');
    } finally {
      setBusy(null);
    }
  };

  const addDropLocation = () => {
    setDrops((prev) => [...prev, emptyDrop()]);
    resetPricing();
  };

  const removeDropLocation = (index) => {
    if (drops.length <= 1) return;
    setDrops((prev) => prev.filter((_, i) => i !== index));
    if (activeMarkerIndex === index) setActiveMarkerIndex(0);
    else if (activeMarkerIndex > index) setActiveMarkerIndex((i) => i - 1);
    resetPricing();
  };

  const updateDropDraft = (index, text) => {
    setDrops((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], draft: text, address: null, marker: null };
      return next;
    });
    resetPricing();
  };

  const loadRates = async (allClasses = true) => {
    if (!pickup?.id || !allDropsResolved) {
      setError('Set pickup and every drop (search, quick pick, or map).');
      return;
    }
    if (!allClasses && !hasVehicleParams) {
      setError('Enter vehicle requirements, or use All classes (1–9).');
      return;
    }
    setError(null);
    setBusy('rates');
    try {
      const data = await fetchQuoteVehicleOptions({
        pickupAddressId: pickup.id,
        dropAddressIds: resolvedDropIds.map(String),
        pickupTime: formatQuotePickupTime(pickupTime),
        allClassesRates: allClasses,
        vehicleParams,
      });
      const options = data.vehicle_options || [];
      if (!options.length) {
        throw new Error(
          'No vehicles are currently available for this route. Please try a different route or contact support.',
        );
      }
      setVehicleOptions(options);
      setRouteMeta(data.route || (options[0] ? {
        distance_km: options[0].distance_km,
        duration_mins: options[0].route_duration_mins,
      } : null));
      setSelectedClasses([]);
      setBookingVehicleIndex(options.length ? 0 : null);
    } catch (e) {
      setError(e.message || 'Could not load vehicle rates');
    } finally {
      setBusy(null);
    }
  };

  const bookingVehicle = bookingVehicleIndex != null ? sortedVehicleOptions[bookingVehicleIndex] : null;

  const quoteVehicleRows = useMemo(() => {
    const available = new Map(
      sortedVehicleOptions.map((v) => [Number(v.vehicle_class), v]),
    );
    return selectedClasses
      .map((cls) => available.get(Number(cls)))
      .filter(Boolean);
  }, [selectedClasses, sortedVehicleOptions]);

  const effectiveDiscountPct = useMemo(
    () => normalizeDiscountPercent(bookingDiscountPercent),
    [bookingDiscountPercent],
  );

  const validateSchedule = () => {
    if (!pickupTime || Number.isNaN(pickupTime.getTime())) {
      setError('Set earliest pickup in Schedule & other details.');
      return false;
    }
    const now = new Date();
    if (pickupTime <= now) {
      setError('Pickup time must be in the future.');
      return false;
    }
    if (isFlexBooking && (!deliveryDeadline || deliveryDeadline <= pickupTime)) {
      setError('Flex booking: set latest pickup after earliest pickup.');
      return false;
    }
    return true;
  };

  const validateBookingRouteAndVehicle = () => {
    if (!customerId) {
      setError('Link a customer to this call before saving a booking.');
      return false;
    }
    if (!pickup?.id || !allDropsResolved) {
      setError('Set pickup and all drop locations first.');
      return false;
    }
    if (bookingVehicleIndex == null || !bookingVehicle) {
      setError('Select a vehicle (tap a rate card — blue border = booking vehicle).');
      return false;
    }
    return true;
  };

  const tryDraftOrConfirm = (mode) => {
    if (busy || bookingSubmitLockRef.current) return;
    if (!validateBookingRouteAndVehicle()) return;
    setDetailsOpen(true);
    if (!validateSchedule()) return;
    setError(null);
    setConfirmBookingMode(mode);
  };

  const runDraftOrConfirm = async (mode) => {
    if (bookingSubmitLockRef.current || busy) return;
    if (!validateBookingRouteAndVehicle() || !validateSchedule()) {
      setDetailsOpen(true);
      setConfirmBookingMode(null);
      return;
    }
    bookingSubmitLockRef.current = true;
    setError(null);
    setBusy(mode === 'draft' ? 'draft' : 'confirm');
    try {
      const payload = buildBookingPayload({
        customerId,
        pickupTime,
        pickupAddressId: pickup.id,
        dropAddressIds: resolvedDropIds,
        vehicleOption: bookingVehicle,
        isFlexBooking,
        deliveryDeadline: isFlexBooking ? deliveryDeadline : null,
        remarks,
        materialDescription,
        sourceLeadId,
        discountFactor: effectiveDiscountPct,
        hasGstin: Boolean(customerGstin),
        isB2b: isB2bCustomer,
        state: mode === 'draft' ? 'Draft' : 'Pending',
        paymentOverride: bookingPriceOverride,
      });
      const data = mode === 'draft'
        ? await saveDraftBooking(payload)
        : await createBooking(payload);
      if (mode === 'draft') setDraftSaved(true);
      else setBookingConfirmed(true);
      showToast?.(
        mode === 'draft'
          ? `Draft saved (#${data.booking_id})`
          : `Booking created (#${data.booking_id})`,
        'success',
      );
      onBookingComplete?.();
      setConfirmBookingMode(null);
    } catch (e) {
      setError(e.message || 'Booking save failed');
    } finally {
      setBusy(null);
      bookingSubmitLockRef.current = false;
    }
  };

  const onVehicleCardClick = (index, vehicleClass) => {
    setBookingVehicleIndex(index);
    setSelectedClasses((prev) => {
      const cls = Number(vehicleClass);
      if (prev.includes(cls)) return prev.filter((c) => c !== cls);
      if (prev.length >= QUOTE_MAX_VEHICLE_CLASSES) {
        showToast?.(`Up to ${QUOTE_MAX_VEHICLE_CLASSES} classes in WhatsApp quote`, 'info');
        return prev;
      }
      return [...prev, cls];
    });
  };

  const handleSend = async () => {
    if (!pickup?.id || !allDropsResolved || !sortedVehicleOptions.length) {
      setError('Resolve route and load rates before sending.');
      return;
    }
    if (!validateSchedule()) {
      setDetailsOpen(true);
      return;
    }
    const available = new Set(sortedVehicleOptions.map((v) => Number(v.vehicle_class)));
    const quoteClasses = selectedClasses.filter((c) => available.has(Number(c)));
    if (!quoteClasses.length) {
      setError('Select 1–3 vehicles for the WhatsApp quote (green highlight).');
      return;
    }
    const dropLabels = drops.map((d) => d.address?.text).filter(Boolean);
    const quoteRates = quoteClasses.map((cls) => {
      const row = sortedVehicleOptions.find((v) => Number(v.vehicle_class) === Number(cls));
      const breakdown = row
        ? getPriceBreakdown(row, effectiveDiscountPct, isB2bCustomer, Boolean(customerGstin))
        : null;
      const computed = breakdown?.grandTotal ?? 0;
      const override = quotePriceOverrides[cls];
      return {
        vehicle_class: Number(cls),
        quoted_price: getEffectiveAmount(computed, override ?? ''),
      };
    });
    setError(null);
    setBusy('send');
    try {
      await sendMemberBookingQuote(
        batchId,
        memberId,
        {
          phone_e164: member?.phone_e164 || member?.phone || '',
          customer_id: member?.customer_id || undefined,
          lead_id: member?.lead_id || undefined,
          pickup_address_id: String(pickup.id),
          drop_address_ids: resolvedDropIds.map(String),
          pickup_time: formatQuotePickupTime(pickupTime),
          pickup_label: pickup.text,
          drop_label: dropLabels[dropLabels.length - 1] || '',
          drop_labels: dropLabels,
          customer_name: customerName,
          vehicle_classes: quoteClasses,
          quote_rates: quoteRates,
          ...vehicleParams,
          all_classes_rates: true,
          marketing_initiated: marketingInitiated,
          ...(refollowMemberId != null && refollowMemberId !== ''
            ? { refollow_member_id: refollowMemberId }
            : {}),
        },
      );
      setQuoteSent(true);
      showToast?.('Booking quote sent on WhatsApp', 'success');
      onSent?.();
    } catch (e) {
      setError(e.message || 'Send failed');
    } finally {
      setBusy(null);
    }
  };

  const canLoadRates = pickup?.id && allDropsResolved && busy !== 'rates';

  const renderQuickPicks = (isPickup, dropIndex = 0) => {
    if (!customerId) return null;
    const list = isPickup
      ? savedAddresses
      : savedAddresses.filter((a) => String(a.id) !== String(pickup?.id));

    return (
      <div className={s.recBlock}>
        <div className={s.recLabel}>Saved addresses (quick pick)</div>
        {savedLoading ? (
          <span className={s.muted}>Loading…</span>
        ) : (
          <div className={s.recChips}>
            {list.length === 0 ? (
              <span className={s.muted}>
                {isPickup ? 'No saved addresses for this customer.' : 'No other saved addresses for drop.'}
              </span>
            ) : (
              list.map((addr) => (
                <button
                  type="button"
                  key={`${isPickup ? 'p' : `d${dropIndex}`}-rec-${addr.id}`}
                  className={s.recChip}
                  onClick={() => applySavedAddress(addr, isPickup, dropIndex)}
                >
                  <span className={s.recChipTitle}>
                    {recommendedAddressTitle(addr)}
                    {addr.is_primary ? <span className={s.primaryBadge}>Home</span> : null}
                  </span>
                  <span className={s.recChipMeta}>
                    <i className="fa fa-map-marker" />
                    {recommendedAddressSubtitle(addr) || 'Saved customer address'}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSuggestions = (forPickup, dropIndex) => {
    const active = forPickup
      ? activeMarkerIndex === -1
      : activeMarkerIndex === dropIndex;
    if (!suggestions.length || !active) return null;
    return (
      <div className={s.suggestions}>
        <p className={s.suggestionsHead}>
          <i className="fa fa-map-marker" />
          Found
          {' '}
          {suggestions.length}
          {' '}
          similar — select one:
        </p>
        {suggestions.map((item, idx) => (
          <button
            type="button"
            key={item.id || `${item.latitude}-${item.longitude}-${idx}`}
            className={s.suggestionItem}
            onClick={() => pickSuggestion(item)}
            disabled={busy === 'suggestion'}
          >
            <strong>{item.locality}</strong>
            <span>
              {[item.city, item.state, item.pin_code].filter(Boolean).join(', ')}
              {item.similarity_score != null ? (
                <>
                  {' '}
                  ·
                  {Math.round(item.similarity_score * 100)}
                  % match
                </>
              ) : null}
            </span>
          </button>
        ))}
        <button type="button" className={s.linkBtn} onClick={() => { setSuggestions([]); setError(null); }}>
          Close suggestions
        </button>
      </div>
    );
  };

  const handleBackdropClose = () => {
    if (busy || bookingSubmitLockRef.current || confirmBookingMode) return;
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className={s.overlay}
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClose}
    >
      <div className={s.panel} onClick={(e) => e.stopPropagation()}>
        <header className={s.headerCompact}>
          <h3>
            Booking &amp; quote
            <span className={s.customerChip}>{customerName || member?.phone_e164 || 'Lead'}</span>
          </h3>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        {error ? <div className={s.errorBanner}>{error}</div> : null}

        <section className={s.sectionTight}>
          <div className={s.locBlock}>
            <label className={s.locLabel}>Pickup</label>
            <div className={s.searchRow}>
              <input
                className={`${s.input}${activeMarkerIndex === -1 ? ` ${s.inputActive}` : ''}`}
                placeholder="Search or map · saved addresses below"
                value={pickupDraft}
                onChange={(e) => {
                  setPickupDraft(e.target.value);
                  setPickup(null);
                  setPickupMarker(null);
                  resetPricing();
                }}
                onFocus={() => setActiveMarkerIndex(-1)}
              />
              <button
                type="button"
                className={s.searchBtn}
                disabled={!!busy || !pickupDraft.trim()}
                onClick={() => runSearch(true)}
              >
                {busy === 'search-pickup' ? '…' : 'Search'}
              </button>
            </div>
            {pickup?.id ? (
              <div className={s.resolvedChip}>
                <i className="fa fa-check-circle" />
                {' '}
                {pickup.text}
              </div>
            ) : null}
            {renderQuickPicks(true)}
            {renderSuggestions(true, 0)}
          </div>

          {drops.map((dropRow, index) => (
            <div className={s.locBlock} key={`drop-${index}`}>
              <div className={s.dropHead}>
                <label className={s.locLabel}>
                  Drop location
                  {' '}
                  {drops.length > 1 ? index + 1 : ''}
                </label>
                {drops.length > 1 ? (
                  <button type="button" className={s.removeDrop} onClick={() => removeDropLocation(index)}>
                    Remove
                  </button>
                ) : null}
              </div>
              <div className={s.searchRow}>
                <input
                  className={`${s.input}${activeMarkerIndex === index ? ` ${s.inputActive}` : ''}`}
                  placeholder="Search or map · quick pick below"
                  value={dropRow.draft}
                  onChange={(e) => updateDropDraft(index, e.target.value)}
                  onFocus={() => setActiveMarkerIndex(index)}
                />
                <button
                  type="button"
                  className={s.searchBtn}
                  disabled={!!busy || !dropRow.draft.trim()}
                  onClick={() => runSearch(false, index)}
                >
                  {busy === `search-drop-${index}` ? '…' : 'Search'}
                </button>
              </div>
              {dropRow.address?.id ? (
                <div className={s.resolvedChip}>
                  <i className="fa fa-check-circle" />
                  {' '}
                  {dropRow.address.text}
                </div>
              ) : null}
              {renderQuickPicks(false, index)}
              {renderSuggestions(false, index)}
            </div>
          ))}

          <button type="button" className={s.addDropBtn} onClick={addDropLocation}>
            <i className="fa fa-plus" />
            Add drop location
          </button>

          <div className={s.mapWrap}>
            <span className={s.mapBadge}>
              {activeMarkerIndex === -1
                ? 'Setting pickup'
                : `Setting drop ${activeMarkerIndex + 1}`}
            </span>
            {loadError ? (
              <p className={s.muted}>Map unavailable — use Search only.</p>
            ) : !isLoaded ? (
              <p className={s.muted}>Loading map…</p>
            ) : (
              <GoogleMap
                mapContainerStyle={MAP_STYLE}
                center={mapView.center}
                zoom={mapView.zoom}
                onLoad={onMapLoad}
                onClick={handleMapClick}
                options={{ streetViewControl: false, mapTypeControl: false, gestureHandling: 'greedy' }}
              >
                {pickupMarker ? (
                  <Marker
                    position={pickupMarker}
                    icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                    title="Pickup"
                  />
                ) : null}
                {drops.map((d, i) => (d.marker ? (
                  <Marker
                    key={`drop-marker-${i}`}
                    position={d.marker}
                    icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                    title={`Drop ${i + 1}`}
                  />
                ) : null))}
              </GoogleMap>
            )}
          </div>

          <div className={s.vehicleReq}>
            <div className={s.reqGrid}>
              <input className={s.input} placeholder="Vehicle type e.g. Tata Ace" value={vehicleType} onChange={(e) => { setVehicleType(e.target.value); resetPricing(); }} />
              <select className={s.input} value={bodyType} onChange={(e) => { setBodyType(e.target.value); resetPricing(); }}>
                <option value="">Body type</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="container">Container</option>
                <option value="flatbed">Flatbed</option>
                <option value="tanker">Tanker</option>
              </select>
              <input className={s.input} placeholder="Weight (T)" value={loadWeight} onChange={(e) => { setLoadWeight(e.target.value); resetPricing(); }} />
              <input className={s.input} placeholder="Length (ft)" value={loadLength} onChange={(e) => { setLoadLength(e.target.value); resetPricing(); }} />
              <input className={s.input} placeholder="Width (ft)" value={loadWidth} onChange={(e) => { setLoadWidth(e.target.value); resetPricing(); }} />
              <input className={s.input} placeholder="Height (ft)" value={loadHeight} onChange={(e) => { setLoadHeight(e.target.value); resetPricing(); }} />
            </div>
          </div>

          <div className={s.rateBtnsInline}>
            <button type="button" className={s.ratesBtnSecondary} disabled={!canLoadRates || !!busy} onClick={() => loadRates(false)}>
              Vehicle options
            </button>
            <button type="button" className={s.ratesBtn} disabled={!canLoadRates || !!busy} onClick={() => loadRates(true)}>
              {busy === 'rates' ? 'Loading…' : 'All classes (1–15)'}
            </button>
          </div>
        </section>

        {sortedVehicleOptions.length > 0 ? (
          <section className={s.sectionTight}>
            <div className={s.ratesHeader}>
              <h4 className={s.sectionTitle}>Rates · classes 1–15</h4>
              {routeMeta ? (
                <span className={s.routeMeta}>
                  <i className="fa fa-road" />
                  {Number(routeMeta.distance_km ?? sortedVehicleOptions[0]?.distance_km).toFixed(1)}
                  km ·
                  <i className="fa fa-clock-o" />
                  {Math.round(routeMeta.duration_mins ?? sortedVehicleOptions[0]?.route_duration_mins ?? 0)}
                  min
                </span>
              ) : null}
            </div>
            {missingClassIds.length > 0 ? (
              <p className={s.hintWarn}>
                Classes
                {' '}
                {missingClassIds.join(', ')}
                {' '}
                had no rate for this route (same pricing API as New Booking).
              </p>
            ) : null}
            <div className={s.vehicleGrid}>
              {Array.from({ length: QUOTE_RATE_CLASS_MAX }, (_, i) => i + 1).map((classId) => {
                const index = sortedVehicleOptions.findIndex(
                  (o) => Number(o.vehicle_class) === classId,
                );
                const v = index >= 0 ? sortedVehicleOptions[index] : null;
                if (!v) {
                  return (
                    <div key={`missing-${classId}`} className={s.vehicleCardDisabled}>
                      <span className={s.vehicleName}>Class {classId}</span>
                      <span className={s.muted}>No rate</span>
                    </div>
                  );
                }
                const cls = Number(v.vehicle_class);
                const selected = selectedClasses.includes(cls);
                const forBooking = bookingVehicleIndex === index;
                return (
                  <button
                    type="button"
                    key={`${cls}-${index}`}
                    className={`${s.vehicleCard}${selected ? ` ${s.vehicleCardSelected}` : ''}${forBooking ? ` ${s.vehicleCardBooking}` : ''}`}
                    onClick={() => onVehicleCardClick(index, cls)}
                  >
                    <div className={s.vehicleCardTop}>
                      <span className={s.vehicleName}>{v.display_vehicle_name || v.vehicle_name}</span>
                      <span className={s.vehiclePrice}>
                        ₹{(() => {
                          const bd = getPriceBreakdown(v, effectiveDiscountPct, isB2bCustomer, Boolean(customerGstin));
                          return bd ? bd.grandTotal.toLocaleString('en-IN') : formatQuoteListPrice(v);
                        })()}
                      </span>
                    </div>
                    <div className={s.vehicleSpecs}>
                      Class
                      {v.vehicle_class}
                      ·
                      {v.payload_tons}
                      T ·
                      {v.length_ft}
                      ft
                    </div>
                    <div className={s.vehicleMeta}>
                      Tolls ₹
                      {Math.ceil(Number(v.toll_price_applied || 0)).toLocaleString('en-IN')}
                      {forBooking ? <span className={s.bookingBadge}>Booking</span> : null}
                      {selected ? (
                        <span className={s.selectedTag}>
                          <i className="fa fa-check" />
                          Quote
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        <button
          type="button"
          className={`${s.accordionHead}${detailsOpen ? ` ${s.accordionHeadOpen}` : ''}`}
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
        >
          <span>
            <i className="fa fa-clipboard" />
            Schedule, flex booking &amp; other details
          </span>
          <i className={`fa fa-chevron-${detailsOpen ? 'up' : 'down'}`} />
        </button>
        {detailsOpen ? (
          <div className={s.accordionBody}>
            <div className={isFlexBooking ? s.datetimeRow : s.datetimeSingle}>
              <div>
                <span className={s.fieldLabel}>Earliest pickup *</span>
                <input
                  type="datetime-local"
                  className={s.input}
                  value={toDatetimeLocalValue(pickupTime)}
                  onChange={(e) => {
                    const d = fromDatetimeLocalValue(e.target.value);
                    if (d) {
                      setPickupTime(d);
                      resetPricing();
                    }
                  }}
                />
              </div>
              {isFlexBooking ? (
                <div>
                  <span className={s.fieldLabel}>Latest pickup (flex) *</span>
                  <input
                    type="datetime-local"
                    className={s.input}
                    value={deliveryDeadline ? toDatetimeLocalValue(deliveryDeadline) : ''}
                    min={toDatetimeLocalValue(pickupTime)}
                    onChange={(e) => setDeliveryDeadline(fromDatetimeLocalValue(e.target.value))}
                  />
                </div>
              ) : null}
            </div>
            <label className={s.flexInline}>
              <input
                type="checkbox"
                checked={isFlexBooking}
                onChange={(e) => {
                  setIsFlexBooking(e.target.checked);
                  if (!e.target.checked) setDeliveryDeadline(null);
                }}
              />
              <span>
                Flex booking (25% off)
                <span className={s.hint} style={{ display: 'block', fontWeight: 400, marginTop: 4 }}>
                  {isFlexBooking
                    ? 'Vehicle may arrive anytime between earliest and latest pickup.'
                    : 'Enable for flexible window + flex discount on base fare (excl. toll).'}
                </span>
              </span>
            </label>
            <span className={s.fieldLabel}>Remarks / special instructions</span>
            <textarea
              className={s.textarea}
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any special instructions for this booking…"
            />
            <span className={s.fieldLabel}>Materials / load description</span>
            <input
              className={s.input}
              style={{ width: '100%' }}
              value={materialDescription}
              onChange={(e) => setMaterialDescription(e.target.value)}
              placeholder="e.g. Cement bags, steel rods (approx 2 tons)"
            />
            <span className={s.fieldLabel}>Discount (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              className={s.input}
              style={{ maxWidth: 120 }}
              value={bookingDiscountPercent}
              onChange={(e) => setBookingDiscountPercent(e.target.value)}
              disabled={isFlexBooking}
            />
            <p className={s.hint}>
              {isFlexBooking
                ? `Flex discount ${normalizeDiscountPercent(bookingDiscountPercent)}% on base fare (excl. toll).`
                : (
                  <>
                    {isB2bCustomer ? 'B2B customer — ' : ''}
                    {customerGstin && !isB2bCustomer ? `GSTIN on file — ` : ''}
                    {customerDiscountPercent > 0
                      ? `Account discount ${customerDiscountPercent}%. Pickup tomorrow+ may auto-apply 10% early booking.`
                      : customerBookingCount === 0
                        ? `First booking welcome discount: ${FIRST_BOOKING_DISCOUNT_PERCENT}% off base fare (excl. toll).`
                        : 'Manual discount optional. Pickup tomorrow+ may suggest 10% early booking.'}
                  </>
                )}
            </p>
          </div>
        ) : null}

        {(bookingVehicle || quoteVehicleRows.length > 0) && vehicleOptions.length > 0 ? (
          <section className={s.summaryCard}>
            <div className={s.summaryHeader}>Summary — quote vs booking</div>
            <p className={s.hint} style={{ margin: '0 0 8px', padding: '0 14px' }}>
              Send WhatsApp quote uses green selections only (no booking created). Save draft / Confirm
              booking uses the blue vehicle and calls customer draft-booking / create-booking.
            </p>
            <div className={s.summaryBody}>
              <div className={s.summaryRow}>
                <span className={s.summaryLabel}>Earliest pickup</span>
                <span className={s.summaryValue}>{pickupTime.toLocaleString()}</span>
              </div>
              {isFlexBooking && deliveryDeadline ? (
                <div className={s.summaryRow}>
                  <span className={s.summaryLabel}>Latest pickup</span>
                  <span className={s.summaryValue}>{deliveryDeadline.toLocaleString()}</span>
                </div>
              ) : null}
              {pickup?.text ? (
                <div className={s.summaryRow}>
                  <span className={s.summaryLabel}>Pickup</span>
                  <span className={s.summaryValue}>{pickup.text}</span>
                </div>
              ) : null}
              {drops.filter((d) => d.address?.text).map((d, index) => (
                <div key={`sum-drop-${index}`} className={s.summaryRow}>
                  <span className={s.summaryLabel}>{`Drop ${index + 1}`}</span>
                  <span className={s.summaryValue}>{d.address.text}</span>
                </div>
              ))}
              {bookingVehicle ? (
                <div className={s.selectedVehicleSummary}>
                  <div className={s.vehicleInfo}>
                    <div className={s.vehicleName}>
                      {`Booking: ${bookingVehicle.display_vehicle_name || bookingVehicle.vehicle_name}`}
                    </div>
                    <div className={s.vehicleDetails}>
                      {bookingVehicle.distance_km} km ·
                      {Math.round(bookingVehicle.route_duration_mins)} min ·
                      {`Class ${bookingVehicle.vehicle_class}`}
                    </div>
                  </div>
                  <div className={s.priceInfo}>
                    {(() => {
                      const breakdown = getPriceBreakdown(
                        bookingVehicle,
                        effectiveDiscountPct,
                        isB2bCustomer,
                        Boolean(customerGstin),
                      );
                      if (!breakdown) return null;
                      const isOverridden = bookingPriceOverride !== ''
                        && parseFloat(bookingPriceOverride) !== breakdown.grandTotal;
                      return (
                        <>
                          {breakdown.discountPercentage > 0 ? (
                            <div className={s.priceLabel}>
                              {breakdown.discountPercentage}% discount (excl. toll): −₹
                              {breakdown.discountAmount.toLocaleString()}
                            </div>
                          ) : null}
                          <div className={s.editablePriceWrapper}>
                            <span className={s.currencySymbol}>₹</span>
                            <input
                              type="number"
                              className={`${s.editablePriceInput}${isOverridden ? ` ${s.overridden}` : ''}`}
                              value={bookingPriceOverride}
                              onChange={(e) => setBookingPriceOverride(e.target.value)}
                              placeholder={breakdown.grandTotal.toLocaleString()}
                              title="Final amount for draft / confirm booking"
                            />
                          </div>
                          {isOverridden ? (
                            <div className={`${s.priceLabel} ${s.overriddenLabel}`}>
                              Overridden (was ₹{breakdown.grandTotal.toLocaleString()})
                            </div>
                          ) : (
                            <div className={s.priceLabel}>Estimated price (draft / confirm)</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : null}
              {quoteVehicleRows.map((row) => {
                const cls = Number(row.vehicle_class);
                const breakdown = getPriceBreakdown(
                  row,
                  effectiveDiscountPct,
                  isB2bCustomer,
                  Boolean(customerGstin),
                );
                const computed = breakdown?.grandTotal ?? 0;
                const override = quotePriceOverrides[cls] ?? '';
                const isOverridden = override !== '' && parseFloat(override) !== computed;
                return (
                  <div key={`quote-sum-${cls}`} className={s.selectedVehicleSummary}>
                    <div className={s.vehicleInfo}>
                      <div className={s.vehicleName}>
                        {`Quote: ${row.display_vehicle_name || row.vehicle_name}`}
                      </div>
                      <div className={s.vehicleDetails}>{`Class ${cls} · WhatsApp quote line`}</div>
                    </div>
                    <div className={s.priceInfo}>
                      <div className={s.editablePriceWrapper}>
                        <span className={s.currencySymbol}>₹</span>
                        <input
                          type="number"
                          className={`${s.editablePriceInput}${isOverridden ? ` ${s.overridden}` : ''}`}
                          value={override}
                          onChange={(e) => setQuotePriceOverrides((prev) => ({
                            ...prev,
                            [cls]: e.target.value,
                          }))}
                          placeholder={computed.toLocaleString()}
                          title="Quoted price sent on WhatsApp"
                        />
                      </div>
                      {isOverridden ? (
                        <div className={`${s.priceLabel} ${s.overriddenLabel}`}>
                          {`Sent rate (was ₹${computed.toLocaleString()})`}
                        </div>
                      ) : (
                        <div className={s.priceLabel}>Quote rate (editable before send)</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        <footer className={s.footer}>
          <button type="button" className={s.secondary} onClick={onClose} disabled={!!busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`${s.footerAction}${draftSaved ? ` ${s.footerSuccess}` : ''}`}
            disabled={!!busy || !vehicleOptions.length}
            onClick={() => tryDraftOrConfirm('draft')}
          >
            {busy === 'draft' ? 'Saving…' : draftSaved ? 'Draft saved' : 'Save draft'}
          </button>
          <button
            type="button"
            className={`${s.footerAction}${bookingConfirmed ? ` ${s.footerSuccess}` : ''}`}
            disabled={!!busy || !vehicleOptions.length}
            onClick={() => tryDraftOrConfirm('confirm')}
          >
            {busy === 'confirm' ? 'Creating…' : bookingConfirmed ? 'Booking confirmed' : 'Confirm booking'}
          </button>
          <button
            type="button"
            className={`${s.footerAction}${quoteSent ? ` ${s.footerSuccess}` : ''}`}
            disabled={!!busy || !vehicleOptions.length}
            onClick={handleSend}
          >
            {busy === 'send' ? 'Sending…' : quoteSent ? 'WhatsApp quote sent' : 'Send WhatsApp quote'}
          </button>
        </footer>

      </div>

      {confirmBookingMode ? (
        <div
          className={s.viewportConfirmOverlay}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setConfirmBookingMode(null);
          }}
        >
          <div className={s.viewportConfirmPanel}>
            <h4>{confirmBookingMode === 'draft' ? 'Save draft booking?' : 'Create booking?'}</h4>
            <p className={s.hint}>
              Uses the blue booking vehicle and amounts from Summary. WhatsApp quote selections are not saved as a booking.
            </p>
            <div className={s.confirmFacts}>
              <div><strong>Pickup:</strong> {pickupTime.toLocaleString()}</div>
              {isFlexBooking && deliveryDeadline ? (
                <div><strong>Latest:</strong> {deliveryDeadline.toLocaleString()}</div>
              ) : null}
              {bookingVehicle ? (
                <div>
                  <strong>Vehicle:</strong>
                  {' '}
                  {bookingVehicle.display_vehicle_name || bookingVehicle.vehicle_name}
                  {' '}
                  (class
                  {' '}
                  {bookingVehicle.vehicle_class}
                  )
                </div>
              ) : null}
            </div>
            <div className={s.viewportConfirmActions}>
              <button
                type="button"
                className={s.secondary}
                disabled={!!busy}
                onClick={() => setConfirmBookingMode(null)}
              >
                Back
              </button>
              <button
                type="button"
                className={s.primary}
                disabled={!!busy}
                onClick={() => runDraftOrConfirm(confirmBookingMode)}
              >
                {busy === 'draft' ? 'Saving…' : busy === 'confirm' ? 'Creating…' : (
                  confirmBookingMode === 'draft' ? 'Save draft' : 'Create booking'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
